import { useRef } from "react";
import { Camera, X, Crown } from "lucide-react";
import { toast } from "sonner";
import { resizeImage } from "@/lib/photo";
import { UpgradeButton } from "@/components/Paywall";

const PHOTO_PROMPTS = ["What does this warning light mean?", "What is leaking here?", "Is this part worn out?"];

export const PhotoButton = ({ isPro, disabled, onPick, onUpsell }) => {
  const ref = useRef(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose a photo.");
    try {
      const blob = await resizeImage(file);
      onPick({ blob, url: URL.createObjectURL(blob) });
    } catch {
      toast.error("Couldn't read that photo. Try a JPEG or PNG.");
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="chat-photo-input" />
      <button type="button" disabled={disabled} onClick={() => (isPro ? ref.current.click() : onUpsell())} aria-label="Add photo" data-testid="chat-photo-btn"
        className="relative h-12 w-12 shrink-0 grid place-items-center border border-[#27303E] hover:border-amber-500 active:scale-95 rounded-sm text-slate-300 disabled:opacity-50 transition-colors duration-200">
        <Camera size={18} />
        {!isPro && <Crown size={11} className="absolute top-1 right-1 text-amber-500" />}
      </button>
    </>
  );
};

export const PhotoPreview = ({ photo, onRemove, onPrompt }) => (
  <div className="px-2.5 sm:px-4 pt-3 border-t border-[#27303E]" data-testid="chat-photo-preview">
    <div className="flex items-start gap-3">
      <div className="relative shrink-0">
        <img src={photo.url} alt="Attached" className="w-16 h-16 object-cover rounded-sm border border-amber-500/60" />
        <button onClick={onRemove} aria-label="Remove photo" data-testid="chat-photo-remove-btn" className="absolute -top-2 -right-2 w-6 h-6 grid place-items-center rounded-full bg-[#0B0D10] border border-[#27303E] text-slate-300 hover:text-red-400">
          <X size={12} />
        </button>
      </div>
      <div className="flex-1 min-w-0 flex gap-2 overflow-x-auto no-scrollbar">
        {PHOTO_PROMPTS.map((p, i) => (
          <button key={p} onClick={() => onPrompt(p)} data-testid={`chat-photo-prompt-${i}`} className="shrink-0 whitespace-nowrap text-xs px-3 h-9 border border-[#27303E] hover:border-amber-500 active:border-amber-500 rounded-sm text-slate-300 transition-colors duration-200">
            {p}
          </button>
        ))}
      </div>
    </div>
  </div>
);

export const PhotoUpsell = ({ onClose }) => (
  <div className="mx-2.5 sm:mx-4 mt-3 p-4 border border-amber-500/50 bg-[#13171F] rounded-sm flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="photo-upsell">
    <div className="text-sm text-slate-300">
      <p className="font-display text-lg font-bold uppercase text-white flex items-center gap-2"><Camera size={16} className="text-amber-500" /> Photo diagnosis is Pro</p>
      Snap a leak, worn part or warning light and get an instant AI diagnosis.
    </div>
    <div className="flex gap-2 shrink-0">
      <UpgradeButton className="h-10 px-4 text-sm" label="Go Pro $9.99" />
      <button onClick={onClose} aria-label="Close" data-testid="photo-upsell-close-btn" className="h-10 w-10 grid place-items-center border border-[#27303E] rounded-sm text-slate-400"><X size={16} /></button>
    </div>
  </div>
);
